/**
 * Theme mode management: light, dark, or system preference.
 */

import { useCallback, useEffect, useState, useSyncExternalStore } from "react";

export type ThemeMode = "light" | "dark" | "system";

const STORAGE_KEY = "trippool-theme";
type Listener = () => void;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of listeners) {
    listener();
  }
}

export function subscribeTheme(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getStoredTheme(): ThemeMode {
  if (typeof window === "undefined") return "system";
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

export function isDarkMode(): boolean {
  if (typeof document === "undefined") return false;
  const current = document.documentElement.dataset.theme;
  if (current === "dark") return true;
  if (current === "light") return false;
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

export function applyTheme(mode: ThemeMode): void {
  if (typeof document === "undefined") return;
  if (mode === "system") {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = mode;
  }
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    // ignore quota/private browsing restriction
  }
  notify();
}

export function toggleTheme(): ThemeMode {
  const willBeDark = !isDarkMode();
  const nextMode: ThemeMode = willBeDark ? "dark" : "light";
  applyTheme(nextMode);
  return nextMode;
}

export function initTheme(): void {
  applyTheme(getStoredTheme());
}

export function useTheme(): {
  theme: ThemeMode;
  isDark: boolean;
  setTheme: (mode: ThemeMode) => void;
  toggleTheme: () => void;
} {
  const theme = useSyncExternalStore(
    subscribeTheme,
    getStoredTheme,
    () => "system" as ThemeMode,
  );
  const [isDark, setIsDark] = useState(() => isDarkMode());

  useEffect(() => {
    setIsDark(isDarkMode());
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => setIsDark(isDarkMode());
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const setThemeHandler = useCallback((mode: ThemeMode) => {
    applyTheme(mode);
    setIsDark(isDarkMode());
  }, []);

  const toggleHandler = useCallback(() => {
    const next = toggleTheme();
    setIsDark(next === "dark");
  }, []);

  return {
    theme,
    isDark,
    setTheme: setThemeHandler,
    toggleTheme: toggleHandler,
  };
}
